import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'

import * as fromRoot from '@tv3/store/state'

/* Store Rental */
import {
  ConfirmDialogService,
  Rental,
  selectAllRentals,
  TimePickerUtils,
  updateRentalInstructions,
} from '@tokeet-frontend/tv3-platform'
import { RentalService } from '@tokeet-frontend/tv3-platform'
import { isSavingRentalInstructions } from '@tokeet-frontend/tv3-platform'

/* Decorators */
import { SaveForm } from '@tokeet-frontend/tv3-platform'

/* Utils */
import { Destroyable } from '@tokeet-frontend/tv3-platform'
import { mapTo, switchMap, take } from 'rxjs/operators'
import { CopyRentalInfoDialogService } from '@tv3/containers/rentals/copy-rental-info-dialog/copy-rental-info-dialog.service'
import { SyncMainUnitData, SyncMainUnitDataConfirmService } from '@tokeet-frontend/rentals'

@Component({
  selector: 'app-rental-details-instructions',
  templateUrl: './rental-details-instructions.component.html',
  styleUrls: ['./rental-details-instructions.component.scss'],
})
export class RentalDetailsInstructionsComponent extends Destroyable {
  _rental: Rental
  @Input() set rental(r: Rental) {
    if (this._rental?.id !== r?.id) {
      this.setForm(r)
    }
    this._rental = r
  }
  get rental() {
    return this._rental
  }

  instructionsForm = this.fb.group({
    checkin: [],
    checkout: [],
    directions: [],
    rules: [],
  })

  specificsForm = this.fb.group({
    wifi_name: [],
    wifi_pass: [],
    key_pickup: [],
    special_inst: [],
    sec_code: [],
  })

  form = this.fb.group({
    checkin: ['', [Validators.required]],
    checkout: ['', [Validators.required]],
    timezone: ['', [Validators.required]],

    instructions: this.instructionsForm,

    specifics: this.specificsForm,

    payment_terms: [''],
    payment_instructions: [''],
  })

  isFormInitialized = false

  isSaving$ = this.store.pipe(select(isSavingRentalInstructions))

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private confirmDialog: ConfirmDialogService,
    private rentalService: RentalService,
    private copyRentalInfoDialog: CopyRentalInfoDialogService,
    private syncMainUnitDataConfirm: SyncMainUnitDataConfirmService
  ) {
    super()
  }

  copy() {
    this.onCopyTerms()
  }

  onCopyTerms() {
    this.store
      .pipe(
        select(selectAllRentals),
        take(1),
        switchMap((rentals) => this.copyRentalInfoDialog.open(R.filter((r) => r.id !== this.rental.id, rentals))),
        switchMap(({ data, rental }) => {
          return this.confirmDialog
            .confirm({
              title: $localize`Copy Terms?`,
              body:
                $localize`This action will overwrite your current terms, are you sure you want to copy terms from ` +
                rental.name +
                `?`,
            })
            .pipe(mapTo(data))
        })
      )
      .subscribe((data) => {
        if (data.checkin) data.checkin = TimePickerUtils.formatTime(data.checkin)
        if (data.checkout) data.checkout = TimePickerUtils.formatTime(data.checkout)
        this.form.patchValue(data)
        this.onSave(this.form)
      })
  }

  private tryFixNumberString(str) {
    if (Number.isNaN(str * 1)) {
      return str
    }

    if (`${str}`[0] !== '0') {
      return str
    }

    return `(${str})`
  }

  save() {
    this.onSave(this.form)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { specifics, instructions, ...formValues } = form.getRawValue()
    if (specifics.sec_code) {
      specifics.sec_code = this.tryFixNumberString(specifics.sec_code)
    }
    if (specifics.key_pickup) {
      specifics.key_pickup = this.tryFixNumberString(specifics.key_pickup)
    }

    this.store.dispatch(
      updateRentalInstructions({
        payload: {
          ...formValues,
          specifics,
          instructions,
          checkin: this.rentalService.hashTime(formValues.checkin, formValues.timezone),
          checkout: this.rentalService.hashTime(formValues.checkout, formValues.timezone),
        },
        rentalId: this.rental.id,
      })
    )
    this.syncMainUnitDataConfirm.tryToSyncData(this.rental, SyncMainUnitData.Instructions).subscribe()
  }

  private setForm(rental: Rental) {
    if (this.isFormInitialized) {
      return
    }
    this.isFormInitialized = true

    this.form.patchValue({
      checkin: TimePickerUtils.formatTime(rental.checkin),
      checkout: TimePickerUtils.formatTime(rental.checkout),
      timezone: RentalService.getTimeZone(rental),

      instructions: rental.instructions,

      specifics: {
        ...rental.specifics,
        sec_code: R.pathOr('', ['sec_code'], rental.specifics).replace(/[\(\)]/g, ''),
        key_pickup: R.pathOr('', ['key_pickup'], rental.specifics).replace(/[\(\)]/g, ''),
      },

      payment_instructions: rental.payment_instructions,
      payment_terms: rental.payment_terms,
    })
  }
}
